import React, { useState } from "react";
import { OverviewTab } from "./overview/OverviewTab";
import { RelatedNursingHomes } from "./RelatedNursingHomes";
import { TabNavigation } from "./page/TabNavigation";
import { NursingHomeSidebar } from "./NursingHomeSidebar";
import { useMediaQuery } from "@/hooks/use-media-query";
import { FacilitiesTab } from "./page/FacilitiesTab";
import { ContactTab } from "./page/ContactTab";
import { NursingHomeMedia } from "./page/NursingHomeMedia";

interface NursingHomeContentProps {
  nursingHome: {
    nh_slug: string;
    county_slug: string;
    center_name: string;
    description?: string;
    short_description?: string;
    standardized_phone_number?: string;
    standardized_address?: string;
    facilities?: string[];
    services?: string[];
    person_in_charge?: string;
    manager_name?: string;
    year_established?: number;
    amenities?: string[];
    room_types?: string[];
    email?: string;
    official_website?: string;
    hiqa_profile_url?: string;
    max_residents?: number;
    max_occupancy?: number;
    category?: string;
    claimed?: boolean;
    county_name?: string;
    main_image_url?: string;
    inspection_rating?: string;
    fair_deal_approved?: boolean;
    last_inspection_date?: string;
    price_range?: string;
  };
  activeTabProp?: string;
}

export function NursingHomeContent({ nursingHome, activeTabProp = "overview" }: NursingHomeContentProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const isMobile = useMediaQuery("(max-width: 768px)");
  
  // Format county name if not provided directly
  const formattedCountyName = nursingHome.county_name || 
    (nursingHome.county_slug && nursingHome.county_slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '));

  // On mobile, show sidebar first, then content
  return (
    <>
      <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <div className="container py-4 sm:py-8">
        {/* On mobile, show sidebar at the top */}
        {isMobile && (
          <div className="mb-6">
            <NursingHomeSidebar nursingHome={nursingHome} />
          </div>
        )}
        
        <div className="grid gap-6 md:gap-8 md:grid-cols-3 lg:grid-cols-[2fr_1fr]">
          {/* Main content area */}
          <div className="md:col-span-2 lg:col-span-1">
            <div className="space-y-6">
              {activeTab === "overview" && (
                <OverviewTab 
                  nursingHome={nursingHome}
                  showShortDescription={false}
                  countyName={formattedCountyName}
                />
              )}
              {activeTab === "facilities" && (
                <FacilitiesTab nursingHome={nursingHome} />
              )}
              {activeTab === "contact" && (
                <ContactTab nursingHome={nursingHome} />
              )}
              {activeTab === "gallery" && (
                <NursingHomeMedia centerName={nursingHome.center_name} nhSlug={nursingHome.nh_slug} imageUrl={nursingHome.main_image_url} email={nursingHome.email} county={nursingHome.county_name} />
              )}
            </div>
          </div>
          
          {/* Desktop sidebar - hidden on mobile since we show it at the top */}
          {!isMobile && (
            <div className="hidden md:block">
              <NursingHomeSidebar nursingHome={nursingHome} />
            </div>
          )}
        </div>

        {/* Related Nursing Homes section - outside the grid layout to span full width */}
        {nursingHome.county_slug && (
          <RelatedNursingHomes 
            currentNhSlug={nursingHome.nh_slug}
            countySlug={nursingHome.county_slug}
            countyName={formattedCountyName}
          />
        )}
      </div>
    </>
  );
}
